import { useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Eraser, PenLine } from "lucide-react";

export default function SignaturePad({ onChange, value, testid = "signature-pad" }) {
  const sigRef = useRef(null);
  const [hasInk, setHasInk] = useState(Boolean(value));
  const [active, setActive] = useState(false); // disabled until first explicit tap

  const handleEnd = () => {
    if (!sigRef.current) return;
    if (sigRef.current.isEmpty()) {
      setHasInk(false);
      onChange?.("");
      return;
    }
    const data = sigRef.current.toDataURL("image/png");
    setHasInk(true);
    onChange?.(data);
  };

  const clear = () => {
    sigRef.current?.clear();
    setHasInk(false);
    onChange?.("");
  };

  const activate = (e) => {
    e?.preventDefault?.();
    setActive(true);
  };

  // Memoize the canvasProps so SignatureCanvas doesn't reset its internal state
  // on every parent re-render (which would erase a signature in progress).
  const canvasProps = useMemo(
    () => ({
      className: "signature-canvas w-full",
      style: {
        width: "100%",
        height: 180,
        pointerEvents: active ? "auto" : "none",
        touchAction: active ? "none" : "auto",
      },
    }),
    [active],
  );

  return (
    <div data-testid={testid} className="space-y-2">
      <div className="relative rounded-xl bg-[var(--vidoc-bg-secondary)] p-1">
        <SignatureCanvas
          ref={sigRef}
          onEnd={handleEnd}
          penColor="#000"
          canvasProps={canvasProps}
        />
        {!active && (
          <button
            type="button"
            data-testid="signature-activate-overlay"
            onClick={activate}
            onTouchStart={activate}
            className="absolute inset-1 rounded-xl bg-white/40 backdrop-blur-[1px] flex flex-col items-center justify-center gap-2 text-[var(--vidoc-primary)] font-medium touch-target"
            aria-label="Activer la zone de signature"
          >
            <PenLine className="w-7 h-7" />
            <span className="text-sm">Toucher pour signer</span>
            <span className="text-xs text-[var(--vidoc-text-secondary)]">
              Le scroll reste libre tant que vous n'avez pas tappé ici
            </span>
          </button>
        )}
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[var(--vidoc-text-secondary)]">
          {hasInk
            ? "Signature enregistrée"
            : active
              ? "Signez avec le doigt ou la souris"
              : "Zone de signature désactivée"}
        </span>
        <button
          data-testid="signature-clear-btn"
          type="button"
          onClick={clear}
          disabled={!hasInk}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[var(--vidoc-danger)] hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent touch-target"
        >
          <Eraser className="w-4 h-4" />
          Effacer
        </button>
      </div>
    </div>
  );
}
